//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

function makeFetcherActions<
  TData,
  TActionShape,
  TRequest extends string,
  TSuccess extends string,
  TFail extends string,
  TErrorCode extends number = number
>(
  requestType: TRequest,
  successType: TSuccess,
  failType: TFail,
  dataHandler: (data: TData) => TActionShape,
) {
  return {
    requested() {
      return {
        type: requestType,
      };
    },
    succeeded(data: TData) {
      return {
        type: successType,
        ...(dataHandler && dataHandler(data) || {}),
      };
    },
    failed(errorMessage: string, errorCode?: TErrorCode) {
      return {
        type: failType,
        message: errorMessage,
        errorCode,
      };
    },
  };
}

interface IFetchActionsFactory {
  /**
   * Produces a set of "init", "succeeded", and "failed" actionCreators with the given action types.
   * Use this method to generate AJAX request options automatically.
   *
   * @param dataHandler Optional function, when provided, lets you specify the input data type & action format.
   */
  <
    TData,
    TActionShape,
    TRequest extends string,
    TSuccess extends string,
    TFail extends string,
    TErrorCode extends number = number
  >(
    requestType: TRequest,
    successType: TSuccess,
    failType: TFail,
    dataHandler: (data: TData) => TActionShape,
  ): {
    requested: () => { type: TRequest };
    succeeded: (data: TData) => { type: TSuccess; } & TActionShape;
    failed: (errorMessage: string, errorCode?: TErrorCode) => { type: TFail; message: string; errorCode: TErrorCode };
  };
  /**
   * Produces a set of "init", "succeeded", and "failed" actionCreators with the given action types.
   * Use this method to generate AJAX request options automatically.
   *
   */
  <TRequest extends string, TSuccess extends string, TFail extends string, TErrorCode extends number = number>(
    requestType: TRequest,
    successType: TSuccess,
    failType: TFail,
  ): {
    requested: () => { type: TRequest };
    succeeded: () => { type: TSuccess; };
    failed: (errorMessage: string, errorCode?: TErrorCode) => { type: TFail; message: string; errorCode: TErrorCode };
  };
}

export const makeFetchActions = makeFetcherActions as IFetchActionsFactory;

/**
 * Get the types of the action creators for the fetch action.
 */
export type FetchActionCreators<T extends {
  requested: () => unknown;
  succeeded: (data?: any) => unknown;
  failed: (errorMessage: string) => unknown;
}> = T['requested'] | T['succeeded'] | T['failed' ];
